import { useState } from "react";
import { Landmark, MapPin, Trees, Store, Library } from "lucide-react";
import { SiteAllocation } from "./SiteAllocation";
import { ParcelAerial } from "./ParcelAerial";
import { Card, Eyebrow, Note, Reveal, Section, SectionHeader, StatusBadge } from "../deck/primitives";

const FACTS = [
  { k: "Property Owner", v: "City of Murrieta", sub: "Based on City discussions and concept package; title verification pending", s: "tbv" },
  { k: "Anticipated Transaction Structure", v: "Long-term ground lease / development agreement", sub: "Or another City-approved public-private development structure", s: "target" },
  { k: "Subject Parcels", v: "Two parcels", sub: "2.72 acres + 9.90 acres", s: "preliminary" },
  { k: "Bounding Streets", v: "Kalmia St · 2nd St · New Clay St · B St", sub: "Downtown-adjacent City-owned site", s: "preliminary" },
  { k: "Zoning Context", v: "Downtown Murrieta Specific Plan 8", sub: "Parcel-level standards and permitted uses still require confirmation", s: "tbv" },
  { k: "Total Site Area", v: "12.62 acres · 549,727 SF", sub: "Legal description to be confirmed by survey / title", s: "preliminary" },
  { k: "Land Basis", v: "No purchase price assumed", sub: "City expected to retain land ownership", s: "preliminary" },
];

const NEARBY = [
  { icon: Store, t: "Historic Downtown Murrieta", d: "Washington Ave commercial corridor" },
  { icon: Landmark, t: "Murrieta Civic Center", d: "City Hall + Town Square Park" },
  { icon: Library, t: "Murrieta Public Library", d: "Civic / community anchor" },
  { icon: Trees, t: "Murrieta Creek corridor", d: "Trail + open-space context" },
];

const LOC2_FACTS = [
  { k: "Property Owner", v: "City of Murrieta", sub: "Town Square civic campus; title verification pending", s: "tbv" },
  { k: "Anticipated Transaction Structure", v: "Long-term ground lease / development agreement", sub: "Or another City-approved public-private development structure", s: "target" },
  { k: "Subject Parcels", v: "Three parcels", sub: "1.73 acres + 1.58 acres + 1.01 acres", s: "preliminary" },
  { k: "Bounding Streets", v: "Town Square · Memorial Way", sub: "North of Town Square Park; Jefferson Ave and Adams Ave nearby", s: "preliminary" },
  { k: "Zoning Context", v: "To be confirmed", sub: "Parcel-level standards and permitted uses require confirmation", s: "tbv" },
  { k: "Total Site Area", v: "4.32 acres · ≈188,179 SF", sub: "Per City parcel exhibit; legal description to be confirmed by survey / title", s: "preliminary" },
  { k: "Land Basis", v: "No purchase price assumed", sub: "City expected to retain land ownership", s: "preliminary" },
];

const NEARBY2 = [
  { icon: Trees, t: "Town Square Park + amphitheater", d: "Event lawn directly south of the site" },
  { icon: Landmark, t: "Murrieta Civic Center", d: "City Hall campus adjacent" },
  { icon: Library, t: "Murrieta Public Library", d: "Civic / community anchor" },
  { icon: Store, t: "Jefferson Ave corridor", d: "Primary arterial on the east edge" },
];

const LocationToggle = ({ loc, setLoc }) => (
  <div data-testid="property-location-toggle" className="inline-flex rounded-full border border-line bg-white p-1 mb-4">
    {[1, 2].map((n) => (
      <button key={n} data-testid={`property-location-${n}-btn`} onClick={() => setLoc(n)}
        className={`px-5 py-2 rounded-full text-sm transition-colors ${loc === n ? "bg-ink text-white" : "text-char hover:text-arq"}`}>
        Location {n}
      </button>
    ))}
  </div>
);

export default function Property() {
  const [loc, setLoc] = useState(1);
  const facts = loc === 1 ? FACTS : LOC2_FACTS;
  return (
    <Section testid="section-property">
      <SectionHeader num="02" eyebrow="Property + Site" title="A City-owned downtown site — without a land purchase." lede="The working strategy assumes the City retains ownership of the land while granting development, leasing and operating rights through a negotiated agreement." />
      <LocationToggle loc={loc} setLoc={setLoc} />
      <div className="grid lg:grid-cols-12 gap-6">
        <Reveal className="lg:col-span-8">
          <Card className="overflow-hidden">
            <ParcelAerial location={loc} />
            <div className="flex items-center justify-between px-6 py-4 text-xs text-steel">
              <span className="flex items-center gap-2"><MapPin size={14} className="text-arq" /> {loc === 1 ? "Parcel lines: Riverside County Assessor GIS · hover a parcel" : "Parcel exhibit: City of Murrieta · hover a parcel"}</span>
              <span className="font-mono">AERIAL</span>
            </div>
          </Card>
        </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4">
            <Card className="p-7">
              <div className="flex items-center justify-between"><Eyebrow>Nearby Commercial + Civic Uses</Eyebrow><StatusBadge s="tbv" /></div>
              <div className="mt-5 space-y-4">
                {(loc === 1 ? NEARBY : NEARBY2).map(({ icon: I, t, d }) => (
                  <div key={t} className="flex items-start gap-4">
                    <span className="h-9 w-9 rounded-full bg-arq-soft grid place-items-center shrink-0"><I size={16} className="text-arq" /></span>
                    <div><div className="text-sm text-ink">{t}</div><div className="text-xs text-steel">{d}</div></div>
                  </div>
                ))}
              </div>
            </Card>
          </Reveal>
      </div>

      <div className="mt-6">
        <Reveal>
          <Card className="divide-y divide-line">
            {facts.map((f) => (
              <div key={f.k} data-testid={`property-fact-${f.k.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="grid sm:grid-cols-12 gap-3 px-7 py-5">
                <div className="sm:col-span-4"><Eyebrow>{f.k}</Eyebrow></div>
                <div className="sm:col-span-6">
                  <div className="text-ink font-medium">{f.v}</div>
                  <div className="text-xs text-steel mt-1">{f.sub}</div>
                </div>
                <div className="sm:col-span-2 sm:text-right"><StatusBadge s={f.s} /></div>
              </div>
            ))}
          </Card>
        </Reveal>
      </div>
      {loc === 1 && <SiteAllocation />}
      <Note testid="property-council-note" className="mt-6">
        City Council has <strong className="text-ink">not</strong> approved any transaction. Ownership, parcel boundaries, legal description and title remain subject to formal diligence.
      </Note>
    </Section>
  );
}
